import React, { useState } from "react";

export default function ClassSignupsModal({
  selectedClass,
  classBookings,
  bookingsLoading,
  activeTab,
  setActiveTab,
  closeModal,
  handleCancelBooking,
  getBookingCounts,
  setShowAddStudent,
  handleAddStudentSuccess,
  token,
  updatePass,
  setToaster,
  onRefetchSignups,
  onToggleOnline,
  onToggleAttended,
  onOpenUserDetails,
  onRemoveFromWaitlist,
}) {
  const [updatingPaidId, setUpdatingPaidId] = useState(null);
  const [updatingOnlineId, setUpdatingOnlineId] = useState(null);
  const [updatingAttendedId, setUpdatingAttendedId] = useState(null);

  const handleTogglePaid = async (booking) => {
    if (!booking.passId || !updatePass || !token) return;
    // Toggle: unpaid (unpaidPass true) -> set paid true; paid (unpaidPass false) -> set paid false
    const newPaidStatus = booking.unpaidPass;
    const msg = newPaidStatus
      ? 'Mark this pass as paid?'
      : 'Mark this pass as not paid?';
    if (!window.confirm(msg)) return;
    setUpdatingPaidId(booking.passId);
    try {
      await updatePass(token, booking.passId, { paid: newPaidStatus });
      setToaster?.({
        message: `Pass marked as ${newPaidStatus ? 'paid' : 'not paid'}`,
        type: 'success',
        show: true,
      });
      onRefetchSignups?.();
    } catch (err) {
      console.error('Error updating pass:', err);
      setToaster?.({
        message: 'Failed to update pass',
        type: 'error',
        show: true,
      });
    } finally {
      setUpdatingPaidId(null);
    }
  };

  const handleToggleOnline = async (booking) => {
    if (!onToggleOnline || !selectedClass) return;
    const newOnline = !booking.online;
    setUpdatingOnlineId(booking._id);
    try {
      await onToggleOnline(selectedClass._id, booking._id, newOnline);
      onRefetchSignups?.();
    } catch (err) {
      // toaster shown by parent
    } finally {
      setUpdatingOnlineId(null);
    }
  };

  const handleToggleAttended = async (booking) => {
    if (!onToggleAttended || !selectedClass) return;
    const newAttended = booking.attended === false ? true : false;
    setUpdatingAttendedId(booking._id);
    try {
      await onToggleAttended(selectedClass._id, booking._id, newAttended);
      onRefetchSignups?.();
    } catch (err) {
      // toaster shown by parent
    } finally {
      setUpdatingAttendedId(null);
    }
  };

  if (!selectedClass) return null;

  const confirmedBookings = classBookings.filter((b) => b.status === "confirmed");
  const onsiteCount = confirmedBookings.filter((b) => !b.online && b.attended !== false).length;
  const onlineConfirmedCount = confirmedBookings.filter((b) => b.online === true && b.attended !== false).length;
  const cancelledCount = classBookings.filter((b) => b.status === "cancelled").length;
  const waitingList = selectedClass.waitingList || [];
  const waitingListCount = waitingList.length;

  const visibleBookings = classBookings.filter((booking) => {
    if (activeTab === "cancelled") return booking.status === "cancelled";
    if (activeTab === "onsite") return booking.status === "confirmed" && !booking.online;
    if (activeTab === "online") return booking.status === "confirmed" && booking.online === true;
    return false;
  });

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={closeModal}
    >
      <div
        className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold">{selectedClass.title}</h2>
            <p className="text-gray-600 mt-1 text-left">
              {new Date(selectedClass.start).toLocaleString('en-GB', {
                timeZone: 'Asia/Jerusalem',
                hour: '2-digit',
                minute: '2-digit',
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              })}
            </p>
            {selectedClass.locationName && (
              <p className="text-gray-600 text-sm text-left">
                📍 {selectedClass.locationName}
              </p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              Class ID: <span className="font-mono">{selectedClass._id}</span>
            </p>
          </div>
          <button
            onClick={closeModal}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ✕
          </button>
        </div>

        {/* Class Info */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <p className="text-sm text-gray-600">
            Capacity: {selectedClass.capacity ?? 'Unlimited'} | Studio: {selectedClass.bookedCount ?? 0} | Online: {onlineConfirmedCount}
          </p>
        </div>

        {/* Add Student Button */}
        <div className="mb-4 flex justify-end">
          <button
            className="bg-brown text-white px-4 py-2 rounded hover:bg-pink"
            onClick={() => setShowAddStudent(true)}
          >
            + Add Student
          </button>
        </div>

        {/* Tabs */}
        <div className="mb-4 border-b border-gray-200">
          <div className="flex">
            <button
              type="button"
              className={`flex-1 py-2 text-sm font-semibold focus:outline-none whitespace-nowrap ${
                activeTab === "onsite"
                  ? "border-b-2 border-brown text-brown"
                  : "text-gray-500"
              }`}
              onClick={() => setActiveTab("onsite")}
              title="Studio"
            >
              🏠 {onsiteCount}
            </button>
            <button
              type="button"
              className={`flex-1 py-2 text-sm font-semibold focus:outline-none whitespace-nowrap ${
                activeTab === "online"
                  ? "border-b-2 border-blue-600 text-blue-700"
                  : "text-gray-500"
              }`}
              onClick={() => setActiveTab("online")}
              title="Online"
            >
              🖥️ {onlineConfirmedCount}
            </button>
            <button
              type="button"
              className={`flex-1 py-2 text-sm font-semibold focus:outline-none whitespace-nowrap ${
                activeTab === "waitlist"
                  ? "border-b-2 border-amber-500 text-amber-700"
                  : "text-gray-500"
              }`}
              onClick={() => setActiveTab("waitlist")}
              title="Waiting list"
            >
              ⏳ {waitingListCount}
            </button>
            <button
              type="button"
              className={`flex-1 py-2 text-sm font-semibold focus:outline-none whitespace-nowrap ${
                activeTab === "cancelled"
                  ? "border-b-2 border-red-400 text-red-600"
                  : "text-gray-500"
              }`}
              onClick={() => setActiveTab("cancelled")}
              title="Cancelled"
            >
              🚫 {cancelledCount}
            </button>
          </div>
        </div>

        {/* Sign-ups List */}
        <div>
          <h3 className="text-lg font-semibold mb-4">
            {activeTab === "onsite" && `Studio (${onsiteCount})`}
            {activeTab === "online" && `Online (${onlineConfirmedCount})`}
            {activeTab === "cancelled" && `Cancelled (${cancelledCount})`}
            {activeTab === "waitlist" && `Waiting List (${waitingListCount})`}
          </h3>

          {activeTab === "waitlist" ? (
            <div className="space-y-3">
              {[...waitingList]
                .sort((a, b) => new Date(a.addedAt) - new Date(b.addedAt))
                .map((entry, idx) => (
                  <div key={entry._id || idx} className="border border-amber-200 rounded-lg p-4 bg-amber-50/40">
                    <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                      <div
                        className={
                          entry.userId?.user_id && onOpenUserDetails
                            ? 'flex-1 min-w-0 cursor-pointer rounded-lg -m-1 p-1 hover:bg-amber-50 transition text-left'
                            : 'flex-1 min-w-0'
                        }
                        role={entry.userId?.user_id && onOpenUserDetails ? 'button' : undefined}
                        tabIndex={entry.userId?.user_id && onOpenUserDetails ? 0 : undefined}
                        title={entry.userId?.user_id && onOpenUserDetails ? 'View user details' : undefined}
                        onClick={() =>
                          entry.userId?.user_id && onOpenUserDetails?.(entry.userId.user_id)
                        }
                        onKeyDown={(e) => {
                          if (!entry.userId?.user_id || !onOpenUserDetails) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onOpenUserDetails(entry.userId.user_id);
                          }
                        }}
                      >
                        <p className="font-semibold break-words">
                          <span className="text-amber-700 mr-1.5">#{idx + 1}</span>
                          {entry.userId?.name || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-600 mt-0.5 break-all">
                          {entry.userId?.email || 'Unknown User'}
                        </p>
                        <p className="text-xs text-gray-500">
                          Joined: {new Date(entry.addedAt).toLocaleString('en-GB', {
                            timeZone: 'Asia/Jerusalem',
                            hour: '2-digit',
                            minute: '2-digit',
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                      {onRemoveFromWaitlist && (
                        <button
                          type="button"
                          onClick={() => onRemoveFromWaitlist(selectedClass._id, entry.userId?._id || entry.userId)}
                          className="text-red-600 hover:text-red-800 font-semibold text-sm px-3 py-1 border border-red-300 rounded hover:bg-red-50 whitespace-nowrap"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              {waitingListCount === 0 && (
                <p className="text-gray-500 text-center py-4">No one on the waiting list</p>
              )}
            </div>
          ) : bookingsLoading ? (
            <p className="text-center py-4">Loading sign-ups...</p>
          ) : (
            <div className="space-y-3">
              {(() => {
                const bookingCounts = getBookingCounts();
                return visibleBookings
                  .sort((a, b) => {
                    const aId = a.userId?._id || a.userId;
                    const bId = b.userId?._id || b.userId;
                    const countDiff = (bookingCounts[bId] || 0) - (bookingCounts[aId] || 0);
                    if (countDiff !== 0) return countDiff;
                    return new Date(b.bookedAt) - new Date(a.bookedAt);
                  })
                  .map((booking) => (
                    <div key={booking._id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                        <div
                          className={
                            booking.userId?.user_id && onOpenUserDetails
                              ? 'flex-1 min-w-0 cursor-pointer rounded-lg -m-1 p-1 hover:bg-gray-50 transition text-left'
                              : 'flex-1 min-w-0'
                          }
                          role={booking.userId?.user_id && onOpenUserDetails ? 'button' : undefined}
                          tabIndex={booking.userId?.user_id && onOpenUserDetails ? 0 : undefined}
                          title={booking.userId?.user_id && onOpenUserDetails ? 'View user details' : undefined}
                          onClick={() =>
                            booking.userId?.user_id && onOpenUserDetails?.(booking.userId.user_id)
                          }
                          onKeyDown={(e) => {
                            if (!booking.userId?.user_id || !onOpenUserDetails) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onOpenUserDetails(booking.userId.user_id);
                            }
                          }}
                        >
                          <p className="font-semibold break-words">{booking.userId?.name || 'N/A'}</p>
                          <p className="text-sm text-gray-600 mt-0.5 break-all">
                            {booking.userId?.email || 'Unknown User'}
                          </p>
                          <p className="text-xs text-gray-600">
                            Booked: {new Date(booking.bookedAt).toLocaleString('en-GB', {
                              timeZone: 'Asia/Jerusalem',
                              hour: '2-digit',
                              minute: '2-digit',
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                          {booking.status === 'cancelled' && booking.canceledAt && (
                            <p className="text-xs text-red-600 mt-0.5">
                              Cancelled: {new Date(booking.canceledAt).toLocaleString('en-GB', {
                                timeZone: 'Asia/Jerusalem',
                                hour: '2-digit',
                                minute: '2-digit',
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </p>
                          )}
                        </div>
                        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:max-w-[45%] sm:justify-end">
                          {booking.autoBooked === true && (
                            <span className="px-2.5 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-800 whitespace-nowrap" title="Auto-booked (regular day)">
                              Auto
                            </span>
                          )}
                          {booking.passId != null ? (
                            <button
                              type="button"
                              onClick={() => handleTogglePaid(booking)}
                              disabled={updatingPaidId === booking.passId}
                              title={booking.unpaidPass ? 'Unpaid — click to mark as paid' : 'Paid — click to mark as unpaid'}
                              className={`text-xl leading-none cursor-pointer hover:scale-110 transition-transform disabled:opacity-50 ${
                                booking.unpaidPass ? 'grayscale opacity-50' : ''
                              }`}
                            >
                              {updatingPaidId === booking.passId ? '…' : '💰'}
                            </button>
                          ) : (
                            <span className="text-xl leading-none grayscale opacity-30" title="No pass">
                              💰
                            </span>
                          )}
                          {booking.status === 'confirmed' && onToggleOnline ? (
                            <button
                              type="button"
                              onClick={() => handleToggleOnline(booking)}
                              disabled={updatingOnlineId === booking._id}
                              title={booking.online ? 'Click to set in-studio' : 'Click to set online'}
                              className={`px-2.5 py-1 rounded text-xs font-semibold whitespace-nowrap cursor-pointer hover:opacity-90 disabled:opacity-50 ${
                                booking.online ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'
                              }`}
                            >
                              {updatingOnlineId === booking._id ? '…' : (booking.online ? '🖥️' : '🏠')}
                            </button>
                          ) : booking.online ? (
                            <span className="px-2.5 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-800 whitespace-nowrap" title="Online">
                              🖥️
                            </span>
                          ) : null}
                          {booking.status === 'confirmed' && onToggleAttended ? (
                            <button
                              type="button"
                              onClick={() => handleToggleAttended(booking)}
                              disabled={updatingAttendedId === booking._id}
                              title={booking.attended === false ? 'Absent — click to mark attended' : 'Attended — click to mark absent (no-show)'}
                              className={`text-xl leading-none cursor-pointer hover:scale-110 transition-transform disabled:opacity-50 ${
                                booking.attended === false ? 'grayscale opacity-50' : ''
                              }`}
                            >
                              {updatingAttendedId === booking._id ? '…' : '✅'}
                            </button>
                          ) : booking.attended === false ? (
                            <span className="text-xl leading-none grayscale opacity-50" title="Absent">
                              ✅
                            </span>
                          ) : null}
                          {booking.status === 'confirmed' && (
                            <button
                              onClick={() => handleCancelBooking(selectedClass._id, booking.userId?.user_id)}
                              title="Cancel"
                              className="text-sm leading-none cursor-pointer hover:scale-110 transition-transform opacity-60 hover:opacity-100"
                            >
                              ❌
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ));
              })()}
              {visibleBookings.length === 0 && (
                <p className="text-gray-500 text-center py-4">No sign-ups in this tab</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}